import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { plural, t } from '@lingui/macro';

import CarouselRightSmall from '@rover/icons/svg/carousel-right-small.svg';
import { Text } from '@rover/kibble/core';
import { ButtonIconSizeMap } from '@rover/kibble/core/Button/Button.common';
import { SpacingSystemType } from '@rover/kibble/core/styledSystemPropTypes';
import ClickableInput from '@rover/kibble/patterns/ClickableInput';
import { SM_MIN } from '@rover/kibble/styles/MediaQuery';
import type { ThemeTextColor, ThemeTextSizes } from '@rover/kibble/styles/themeTypes';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import { PetType } from '@rover/types/src/Pet';

import type { TriggeredFrom } from './analyticsEvents';
import { PetCountInputCloseEvent, PetCountInputOpenEvent } from './analyticsEvents';
import PetCounterDesktop from './PetCounterDesktop';
import PetCounterMobile from './PetCounterMobile';

const petCounterTitleText = (): string => t`How many pets need care?`;
const petCounterSubtitleText = (): string =>
  t`Add at least one pet. This helps you find a sitter and see the most accurate pricing.`;
const petCounterSaveButtonText = (): string => t`Save pets`;

export type PetCounterProps = {
  inputLabel?: string;
  inputLabelSize?: ThemeTextSizes;
  inputLabelColor?: ThemeTextColor;
  inputLabelMarginBottom?: SpacingSystemType;
  hideHelperText?: boolean;
  isHomePage?: boolean;
  dogCount?: number;
  catCount?: number;
  puppyCount?: number;
  availablePets?: string[];
  triggeredFrom?: TriggeredFrom;
  onChange: (arg0: {
    petType: PetType[];
    counter: number;
    dogCount: number;
    catCount: number;
    puppyCount: number;
  }) => void;
};

const PetCounter: FC<PetCounterProps> = ({
  dogCount = 0,
  catCount = 0,
  puppyCount = 0,
  hideHelperText = false,
  onChange,
  availablePets,
  inputLabel,
  inputLabelSize,
  inputLabelColor,
  inputLabelMarginBottom,
  isHomePage = false,
  triggeredFrom = 'Search',
}) => {
  const [petsCounterOpen, setPetsCounterOpen] = useState(false);
  const { setScreenReaderAnnouncement } = useA11yContext();
  // Choose the bottom sheet vs. desktop modal by viewport width, not user agent.
  // UA detection (isMobileBrowser) misclassifies tablets, iPads and Android
  // WebViews as non-mobile, so they got the desktop modal, whose fixed width
  // overflows narrow screens and gets cut off (DEV-150107). useMatchMedia returns
  // undefined on first render, so this safely defaults to the bottom sheet until
  // the viewport is measured on the client.
  const isMobile = !useMatchMedia(`(min-width:${SM_MIN}px)`);

  const brandSpecificPetTypes = useBrandSpecificPets();
  const availablePetTypes = availablePets ?? brandSpecificPetTypes;

  const [dogs, setDogs] = useState(dogCount);
  const [cats, setCats] = useState(catCount);
  const [puppies, setPuppies] = useState(puppyCount);

  const NO_PETS_STRING = t`Select your pets`;

  useEffect(() => {
    if (petsCounterOpen) {
      setDogs(dogCount);
      setCats(catCount);
      setPuppies(puppyCount);
    }
  }, [catCount, dogCount, petsCounterOpen, puppyCount]);

  const petString = useMemo(() => {
    if (dogCount === 0 && catCount === 0 && puppyCount === 0) {
      return NO_PETS_STRING;
    }

    const petCountLabels: string[] = [];

    if (availablePetTypes.includes(PetType.DOG)) {
      petCountLabels.push(
        plural(dogCount, {
          one: '# dog',
          other: '# dogs',
        })
      );
    }

    if (availablePetTypes.includes(PetType.PUPPY)) {
      petCountLabels.push(
        plural(puppyCount, {
          one: '# puppy',
          other: '# puppies',
        })
      );
    }

    if (availablePetTypes.includes(PetType.CAT)) {
      petCountLabels.push(
        plural(catCount, {
          one: '# cat',
          other: '# cats',
        })
      );
    }

    return petCountLabels.join(', ');
  }, [dogCount, catCount, puppyCount, availablePetTypes, NO_PETS_STRING]);

  const petStringColor = useMemo<ThemeTextColor>(() => {
    return petString === NO_PETS_STRING ? 'tertiary' : 'primary';
  }, [petString]);

  const handlePetCounterChange = useCallback((): void => {
    const petCounts = {
      [PetType.DOG]: dogs,
      [PetType.CAT]: cats,
      [PetType.PUPPY]: puppies,
    };

    const newPetTypes: PetType[] = (
      Object.keys(petCounts) as Array<(typeof PetType)[keyof typeof PetType]>
    ).reduce<PetType[]>((acc, type) => {
      if (type === PetType.PUPPY && petCounts[type] > 0) {
        return [...acc, PetType.DOG];
      }

      if (petCounts[type] > 0) {
        return [...acc, type];
      }

      return acc;
    }, []);

    // Make values unique
    const uniquePetTypes = Array.from(new Set(newPetTypes));

    setScreenReaderAnnouncement(
      t({
        message: `How many pets? ${petString}`,
        comment:
          'The string will evaluate to something like: How many pets? 1 dog, 1 cat, 0 puppies.',
      })
    );

    const petsSelected = dogs > 0 || cats > 0 || puppies > 0 || isHomePage;
    // avoid triggering on change when all pets are 0
    if (petsSelected) {
      onChange({
        petType: uniquePetTypes,
        counter: Math.min(dogs + cats + puppies, 3),
        dogCount: dogs,
        catCount: cats,
        puppyCount: puppies,
      });
    }
  }, [dogs, cats, puppies, setScreenReaderAnnouncement, petString, isHomePage, onChange]);

  const closePetCounter = (): void => {
    emitAnalyticsEvent(new PetCountInputCloseEvent({ triggeredFrom }));
    setPetsCounterOpen(false);
  };

  const openPetCounter = (): void => {
    emitAnalyticsEvent(new PetCountInputOpenEvent({ triggeredFrom }));
    setPetsCounterOpen(!petsCounterOpen);
  };

  return (
    <div data-testid="pet-counter">
      <ClickableInput
        inputLabel={inputLabel ?? t`How many pets?`}
        onClick={openPetCounter}
        hideIcon={isMobile}
        pointerCursor
        icon={
          <CarouselRightSmall
            width={ButtonIconSizeMap.default}
            height={ButtonIconSizeMap.default}
            fill="currentColor"
          />
        }
        {...(!hideHelperText && {
          helperText: t`For a more accurate search, select at least 1 pet`,
        })}
        {...(inputLabelSize ? { inputLabelSize } : {})}
        {...(inputLabelMarginBottom ? { inputLabelMarginBottom } : {})}
        {...(inputLabelColor ? { inputLabelColor } : {})}
      >
        <Text size="200" textColor={petStringColor}>
          {petString}
        </Text>
      </ClickableInput>
      {isMobile ? (
        <PetCounterMobile
          isBottomSheetOpen={petsCounterOpen}
          dogs={dogs}
          cats={cats}
          puppies={puppies}
          availablePets={availablePetTypes}
          triggeredFrom={triggeredFrom}
          setDogs={setDogs}
          setPuppies={setPuppies}
          setCats={setCats}
          handlePetCounterChange={handlePetCounterChange}
          closeBottomSheet={closePetCounter}
          titleText={petCounterTitleText()}
          subtitleText={petCounterSubtitleText()}
          saveButtonText={petCounterSaveButtonText()}
          closeButtonText={t`Close`}
        />
      ) : (
        <PetCounterDesktop
          isModalOpen={petsCounterOpen}
          dogs={dogs}
          cats={cats}
          puppies={puppies}
          availablePets={availablePetTypes}
          triggeredFrom={triggeredFrom}
          setDogs={setDogs}
          setPuppies={setPuppies}
          setCats={setCats}
          handlePetCounterChange={handlePetCounterChange}
          closeModal={closePetCounter}
          titleText={petCounterTitleText()}
          subtitleText={petCounterSubtitleText()}
          saveButtonText={petCounterSaveButtonText()}
        />
      )}
    </div>
  );
};

export default PetCounter;
